import type { CSSProperties, RefObject } from "react";
import { layerClass } from "@freightsimple/ui-core/Theme/layers";
import { T } from "@freightsimple/ui-core/Typography/T";

/**
 * Break glass: the disclaimer floating over the top-right of the map. The arc we
 * draw between pickup and delivery is decorative, not the carrier's real routing,
 * so we say so — layered on top of the Mapbox canvas, which means absolute
 * positioning over a third-party surface.
 *
 * Scoped `dark`, because what sits behind it is Mapbox's own dark atmosphere in
 * either theme rather than one of our surfaces — so `muted` resolves to the
 * light ink that reads on it, instead of the near-black this used to inherit
 * from the page. It is an aside; readable and no louder than that.
 *
 * `raised` clears the canvas and nothing else. At `z-[100]` it painted through the
 * account menu hanging down from the nav above it.
 */
function NotActualRoute_() {
  return (
    <div
      className={`dark absolute top-[5px] right-[10px] ${layerClass.raised}`}
    >
      <T size="xs" color="muted">
        Disclaimer: Not actual route ;)
      </T>
    </div>
  );
}

interface MapsFullBleedMapCanvasProps {
  /** The element Mapbox GL takes over and renders the map into. */
  containerRef: RefObject<HTMLDivElement | null>;
  /** Px pulled off the top, when the caller wants the map to ride up under the chrome. */
  offsetPx: number;
  /** Add the "not actual route" note — only meaningful once both ends are plotted. */
  showRouteDisclaimer: boolean;
}

/**
 * Break glass: the full-bleed box a Mapbox GL map is mounted into.
 *
 * Mapbox needs a real element with a real size to attach to, and this one is
 * deliberately absolutely positioned and pinned to both edges of the window so
 * the map spans it — escaping the router's content padding — while a same-height
 * spacer keeps the page flowing around it. An absolute escape from an ancestor's
 * padding and a caller-computed top offset are raw geometry with no semantic
 * prop behind them.
 */
export function MapsFullBleedMapCanvas({
  containerRef,
  offsetPx,
  showRouteDisclaimer,
}: MapsFullBleedMapCanvasProps) {
  // Full-bleed: anchored to the page's left edge, escaping the app router's
  // content padding. No negative margin — left: 0 already lands on the page
  // origin, and a margin here just shifts the map off-screen and leaves a white
  // strip on the right. Pinned with left/right instead of width: 100vw, because
  // 100vw counts the vertical scrollbar and pushes the page sideways.
  const canvas: CSSProperties = {
    height: "292px",
    left: 0,
    right: 0,
    position: "absolute",
    marginTop: `-${offsetPx}px`,
  };

  return (
    <div className="h-[292px]">
      <div ref={containerRef} style={canvas}>
        {showRouteDisclaimer && <NotActualRoute_ />}
      </div>
    </div>
  );
}
